import React, { useRef, useState } from "react"
import useSWR from "swr"
import Filter from "./Filter"
import Item from "./Item"

declare global {
    interface Window {
        local_occasions_object: {
            asset: string
            is_corporate: boolean
        }
    }
}

export interface Taxonomy {
    term_id: number
    name: string
    slug: string
    count: number
}

export interface Occasion {
    id: number
    featured_image?: string
    autoid: string
    title: string
    soort: "1"
    supplier: {
        term_id: number
        name: string
    }
    kenteken: string
    category: Taxonomy
    model: string
    color: Taxonomy
    enginecc: string
    power: string
    carversion: string
    kilometers: number
    url: string
    year: string
    apk?: string
    fuel: Taxonomy
    warranty: string
    type: Taxonomy
    milieulabel?: Taxonomy
    transmission: Taxonomy
    monthly?: string
    price?: string
    is_sold: boolean
    export?: number
    rijklaar: boolean
    weight: string
    rijklaargewicht?: string
    wijziging: string
    // more...
    description: string
    images: { url: string }[]
}

export interface Filters {
    supplier: string[]
    category: number[]
    color: number[]
    fuel: number[]
    milieulabel: number[]
    transmission: number[]
    type: number[]
    year_start: number | undefined
    year_end: number | undefined
    price_start: number | undefined
    price_end: number | undefined
    kilometers_start: number | undefined
    kilometers_end: number | undefined
}

export type Sorting =
    | "created_desc"
    | "created_asc"
    | "price_desc"
    | "price_asc"
    | "kilometers_desc"
    | "kilometers_asc"
    | "power_desc"
    | "power_asc"
    | "year_desc"
    | "year_asc"

export default function App() {
    const { data, error } = useSWR<Occasion[]>(
        "/wp-json/autosoft/v1/occasions",
        (url) => fetch(url).then((res) => res.json())
    )
    const [filters, setFilters] = useState<Filters>({
        supplier: [],
        category: [],
        color: [],
        fuel: [],
        milieulabel: [],
        transmission: [],
        type: [],
        year_start: undefined,
        year_end: undefined,
        price_start: undefined,
        price_end: undefined,
        kilometers_start: undefined,
        kilometers_end: undefined,
    })
    const [sorting, setSorting] = useState<Sorting>("created_desc")
    const ref = useRef(null)

    let occasions = data || []

    function toggleFilter(
        type: "checkbox" | "select" | "checkbox-location-based",
        key: string,
        value: number
    ) {
        if (ref.current) {
            const scrollTop = ref.current.offsetTop - 50

            window.scrollTo({
                top: scrollTop,
                behavior: "smooth",
            })
        }

        let current = filters[key]
        if (type === "checkbox") {
            if (current.includes(value)) {
                current = current.filter((it) => it !== value)
            } else {
                current = [...current, value]
            }

            setFilters((prev) => ({
                ...prev,
                [key]: [...current],
            }))
        } else {
            current = value

            setFilters((prev) => ({
                ...prev,
                [key]: current,
            }))
        }
    }

    function handleFilter(occasion: Occasion) {
        const bools = []

        for (const key of Object.keys(filters)) {
            if (key.includes("_start") || key.includes("_end")) {
                continue
            }

            if (filters[key].length > 0) {
                bools.push(filters[key].includes(occasion[key]?.term_id))
            }
        }

        return !bools.includes(false)
    }

    function handleSelectFilter(occasions: Occasion[]) {
        for (const key of Object.keys(filters)) {
            if (!key.includes("_start") && !key.includes("_end")) continue
            const keyWithout = key.replace("_start", "").replace("_end", "")

            occasions = occasions.filter((occasion) => {
                const converted = parseInt(occasion[keyWithout])

                if (
                    filters[`${keyWithout}_start`] &&
                    filters[`${keyWithout}_end`]
                ) {
                    return (
                        filters[`${keyWithout}_start`] <= converted &&
                        filters[`${keyWithout}_end`] >= converted
                    )
                }

                if (filters[`${keyWithout}_start`]) {
                    return filters[`${keyWithout}_start`] <= converted
                }

                if (filters[`${keyWithout}_end`]) {
                    return filters[`${keyWithout}_end`] >= converted
                }

                return true
            })
        }

        return occasions
    }

    function resetFilters() {
        setFilters({
            supplier: [],
            category: [],
            color: [],
            fuel: [],
            milieulabel: [],
            transmission: [],
            type: [],
            year_start: undefined,
            year_end: undefined,
            price_start: undefined,
            price_end: undefined,
            kilometers_start: undefined,
            kilometers_end: undefined,
        })
    }

    occasions = React.useMemo(
        () =>
            handleSelectFilter(occasions.filter(handleFilter)).sort((a, b) => {
                if (sorting === "created_desc")
                    return (
                        new Date(b.wijziging).getTime() -
                        new Date(a.wijziging).getTime()
                    )
                if (sorting === "created_asc")
                    return (
                        new Date(a.wijziging).getTime() -
                        new Date(b.wijziging).getTime()
                    )
                if (sorting === "price_desc") {
                    return parseInt(b.price) - parseInt(a.price)
                }
                if (sorting === "price_asc") {
                    return parseInt(a.price) - parseInt(b.price)
                }
                if (sorting === "kilometers_desc") {
                    return b.kilometers - a.kilometers
                }
                if (sorting === "kilometers_asc") {
                    return a.kilometers - b.kilometers
                }
                if (sorting === "power_desc") {
                    return parseInt(b.power) - parseInt(a.power)
                }
                if (sorting === "power_asc") {
                    return parseInt(a.power) - parseInt(b.power)
                }
                if (sorting === "year_desc") {
                    return parseInt(b.year) - parseInt(a.year)
                }
                if (sorting === "year_asc") {
                    return parseInt(a.year) - parseInt(b.year)
                }

                return a.title.length - b.title.length
            }),
        [filters, occasions, sorting]
    )

    if (!data) return <div>Laden...</div>

    if (error)
        return (
            <div>
                Sorry er is iets fout gegaan met het ophalen van de occasions..
            </div>
        )

    return (
        <div
            ref={ref}
            className="grid lg:grid-cols-4 grid-cols-1 items-start gap-6 mb-10"
        >
            <Filter
                occasions={occasions}
                onReset={resetFilters}
                onChange={toggleFilter}
                filters={filters}
            />

            <div className="col-span-3">
                <div className="mt-3 mb-5 flex lg:flex-row flex-col justify-between lg:items-center">
                    <h4 className="flex lg:mb-0 mb-4">
                        Resultaten{" "}
                        <span className="text-sm text-[#8B8B8B] font-normal ml-5 relative top-2">
                            {occasions.length} auto's gevonden
                        </span>
                    </h4>

                    <div className="flex lg:items-center lg:flex-row flex-col lg:max-w-none max-w-[150px]">
                        <strong className="mr-5 text-[#8B8B8B]">
                            Sorteren op:
                        </strong>
                        <div className="select-wrapper with-chevron">
                            <select
                                value={sorting}
                                onChange={(e) =>
                                    setSorting(e.target.value as Sorting)
                                }
                                className=" py-1 px-2 text-sm"
                                name="sort"
                            >
                                <option value="created_desc">Nieuwste</option>
                                <option value="created_asc">Oudste</option>
                                <option value="price_asc">
                                    Prijs oplopend
                                </option>
                                <option value="price_desc">
                                    Prijs aflopend
                                </option>
                                <option value="kilometers_asc">
                                    Kilometerstand oplopend
                                </option>
                                <option value="kilometers_desc">
                                    Kilometerstand aflopend
                                </option>
                                <option value="year_asc">
                                    Bouwjaar oplopend
                                </option>
                                <option value="year_desc">
                                    Bouwjaar aflopend
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 grid-cols-2 gap-5">
                    {occasions.map((occasion) => (
                        <Item key={occasion.id} occasion={occasion} />
                    ))}
                </div>
            </div>
        </div>
    )
}
